package it.neckar.lizergy.model.energy

import it.neckar.open.formatting.decimalFormat2digits
import it.neckar.open.i18n.CurrentI18nConfiguration
import it.neckar.open.i18n.I18nConfiguration
import it.neckar.open.kotlin.lang.WhitespaceConfig
import it.neckar.open.unit.si.MOhms
import it.neckar.open.unit.si.Ohms
import kotlinx.serialization.Serializable
import kotlin.jvm.JvmInline

/**
 * The resistance in [Ohms]
 */
@Serializable
@JvmInline
value class Resistance internal constructor(
  val ohms: @Ohms Double,
) : Comparable<Resistance> {

  val megaOhms: @MOhms Double
    get() = ohms / 1_000_000.0

  fun formatOhms(i18nConfiguration: I18nConfiguration = CurrentI18nConfiguration, whitespaceConfig: WhitespaceConfig = WhitespaceConfig.NonBreaking): String {
    return "${decimalFormat2digits.format(ohms, i18nConfiguration)}${whitespaceConfig.smallSpace}Ω"
  }

  fun formatMegaOhms(i18nConfiguration: I18nConfiguration = CurrentI18nConfiguration, whitespaceConfig: WhitespaceConfig = WhitespaceConfig.NonBreaking): String {
    return "${decimalFormat2digits.format(megaOhms, i18nConfiguration)}${whitespaceConfig.smallSpace}MΩ"
  }

  override operator fun compareTo(other: Resistance): Int {
    return this.ohms.compareTo(other.ohms)
  }

  operator fun minus(other: Resistance): Resistance {
    return Resistance(this.ohms - other.ohms)
  }

  operator fun plus(other: Resistance): Resistance {
    return Resistance(this.ohms + other.ohms)
  }

  operator fun times(factor: Double): Resistance {
    return Resistance(ohms * factor)
  }

  operator fun times(factor: Int): Resistance {
    return Resistance(ohms * factor)
  }

  override fun toString(): String {
    return formatOhms(I18nConfiguration.Germany, WhitespaceConfig.Spaces)
  }


  companion object {
    val Zero: Resistance = Resistance(0.0)

    fun ohms(ohms: @Ohms Double): Resistance {
      return Resistance(ohms)
    }

    fun megaOhms(megaOhms: @MOhms Double): Resistance {
      return Resistance(megaOhms * 1_000_000.0)
    }
  }
}

/**
 * Converts a value to [ohms]
 */
val @Ohms Int.ohms: Resistance
  get() = Resistance.ohms(this.toDouble())

val @Ohms Double.ohms: Resistance
  get() = Resistance.ohms(this)

val @MOhms Int.megaOhms: Resistance
  get() = Resistance.megaOhms(this.toDouble())

val @MOhms Double.megaOhms: Resistance
  get() = Resistance.megaOhms(this)

/**
 * Calculates the sum of power ratings
 */
fun Iterable<Resistance>.sum(): Resistance {
  return sumOf {
    it.ohms
  }.ohms
}
